import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Target, TrendingUp, Pause } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { useCurrency } from '@/hooks/useCurrency';
import {
  fetchGoals,
  createGoal,
  updateGoal,
  deleteGoal,
  contributeToGoal,
} from '@/features/goals/goalsSlice';
import GoalCard from '@/components/goals/GoalCard';
import GoalForm from '@/components/goals/GoalForm';
import type { GoalFormData } from '@/components/goals/GoalForm';
import ContributeDialog from '@/components/goals/ContributeDialog';
import type { Goal } from '@/lib/types';
import PageGuide from '@/components/common/PageGuide';
import { goalsGuide } from '@/components/common/guideContent';
import { useFABContext } from '@/components/common/FABContext';

export default function Goals() {
  useDocumentTitle('Goals');
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { fmt } = useCurrency();
  const { goals, isLoading } = useAppSelector((state) => state.goals);

  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);

  // [T-FAB] Register quick-add action so the global FAB can open this form
  const { registerFABActions } = useFABContext();
  useEffect(() => {
    registerFABActions({ onAddGoal: () => { setEditingGoal(null); setShowForm(true); } });
    return () => registerFABActions({});
  }, [registerFABActions]);

  // [T-FAB-DRAFT] Auto-open add form when navigating via a draft link from FAB
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if ((location.state as { openForm?: boolean } | null)?.openForm) {
      setEditingGoal(null);
      setShowForm(true);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [contributingGoal, setContributingGoal] = useState<Goal | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formLoading, setFormLoading] = useState(false);
  const [contributeLoading, setContributeLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchGoals());
  }, [dispatch]);

  const handleCreate = async (data: GoalFormData) => {
    setFormLoading(true);
    try {
      await dispatch(
        createGoal({
          ...data,
          targetDate: data.targetDate?.toISOString(),
        })
      ).unwrap();
      toast({ title: 'Goal created', description: 'Your goal has been saved.' });
      setShowForm(false);
      dispatch(fetchGoals());
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (data: GoalFormData) => {
    if (!editingGoal) return;
    setFormLoading(true);
    try {
      await dispatch(
        updateGoal({
          id: editingGoal.id,
          data: {
            ...data,
            targetDate: data.targetDate?.toISOString(),
          },
        })
      ).unwrap();
      toast({ title: 'Goal updated' });
      setEditingGoal(null);
      dispatch(fetchGoals());
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await dispatch(deleteGoal(deletingId)).unwrap();
      toast({ title: 'Goal deleted' });
      setDeletingId(null);
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    }
  };

  const handleContribute = async (goalId: string, amount: number) => {
    setContributeLoading(true);
    try {
      await dispatch(contributeToGoal({ id: goalId, amount })).unwrap();
      toast({
        title: 'Contribution added',
        description: `${fmt(amount)} contributed successfully.`,
      });
      setContributingGoal(null);
      dispatch(fetchGoals());
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setContributeLoading(false);
    }
  };

  // Filter goals
  const filteredGoals =
    statusFilter === 'all'
      ? goals
      : goals.filter((g) => g.status === statusFilter);

  // Summary stats
  const activeCount = goals.filter((g) => g.status === 'active').length;
  const totalSaved = goals.reduce((sum, g) => sum + g.currentAmount, 0);
  const totalTarget = goals.reduce((sum, g) => sum + g.targetAmount, 0);
  const pausedCount = goals.filter((g) => g.status === 'paused').length;

  return (
    <div className="space-y-4 md:space-y-8">
      <PageGuide pageKey="goals" content={goalsGuide} />
      {/* Header */}
      <div className="flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Goals</h2>
          <p className="text-sm md:text-base text-muted-foreground">Track your financial goals</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Goal
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
        <Card className="glass-panel transition-all duration-200 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Goals</CardTitle>
            <div className="p-2 bg-primary/10 rounded-full">
              <Target className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{activeCount}</div>
            <p className="text-xs text-muted-foreground mt-1">In progress</p>
          </CardContent>
        </Card>

        <Card className="glass-panel transition-all duration-200 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Saved</CardTitle>
            <div className="p-2 bg-green-500/10 rounded-full">
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold truncate">
              {fmt(totalSaved)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Across all goals</p>
          </CardContent>
        </Card>

        <Card className="glass-panel transition-all duration-200 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Target Amount</CardTitle>
            <div className="p-2 bg-orange-500/10 rounded-full">
              <Pause className="h-4 w-4 text-orange-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold truncate">
              {fmt(totalTarget)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {pausedCount > 0 ? `${pausedCount} paused` : 'Combined targets'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Goals</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Goals Grid */}
      {isLoading && goals.length === 0 ? (
        <div className="grid gap-3 md:gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-56 w-full" />
          ))}
        </div>
      ) : filteredGoals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Target className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold">
              {goals.length === 0 ? 'No goals yet' : 'No matching goals'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              {goals.length === 0
                ? 'Set your first financial goal and start tracking your progress.'
                : 'Try changing the filter to see more goals.'}
            </p>
            {goals.length === 0 && (
              <Button onClick={() => setShowForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Goal
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:gap-4 md:grid-cols-2">
          {filteredGoals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onEdit={(g) => setEditingGoal(g)}
              onDelete={(id) => setDeletingId(id)}
              onContribute={(g) => setContributingGoal(g)}
            />
          ))}
        </div>
      )}

      {/* Create Form */}
      <GoalForm
        open={showForm}
        onOpenChange={setShowForm}
        onSubmit={handleCreate}
        isLoading={formLoading}
      />

      {/* Edit Form */}
      <GoalForm
        open={!!editingGoal}
        onOpenChange={(open) => !open && setEditingGoal(null)}
        onSubmit={handleUpdate}
        goal={editingGoal}
        isLoading={formLoading}
      />

      {/* Contribute Dialog */}
      <ContributeDialog
        open={!!contributingGoal}
        onOpenChange={(open) => !open && setContributingGoal(null)}
        goal={contributingGoal}
        onContribute={handleContribute}
        isLoading={contributeLoading}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Goal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this goal? All progress and contribution
              history will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
